export interface LoanScenario {
  id: string
  name: string
  loanAmount: number
  interestRate: number
  termYears: number
  downPayment: number
  propertyValue: number
  productType: 'Fixed' | 'Variable' | 'ARM'
}

export interface CalculationResult {
  monthlyPayment: number
  totalPayment: number
  totalInterest: number
  ltv: number // Loan to Value ratio
  dti: number // Debt to Income ratio
  isCompliant: boolean
  violations: string[]
}

export const calculateMortgage = (
  scenario: LoanScenario,
  monthlyIncome: number,
  monthlyDebts: number,
): CalculationResult => {
  const { loanAmount, interestRate, termYears, propertyValue } = scenario

  // Monthly Interest Rate
  const monthlyRate = interestRate / 100 / 12
  const numberOfPayments = termYears * 12

  // Monthly Principal & Interest Payment
  // Formula: M = P [ i(1 + i)^n ] / [ (1 + i)^n – 1 ]
  let monthlyPayment = 0
  if (interestRate === 0) {
    monthlyPayment = loanAmount / numberOfPayments
  } else {
    monthlyPayment =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1)
  }

  const totalPayment = monthlyPayment * numberOfPayments
  const totalInterest = totalPayment - loanAmount

  // LTV Calculation
  const ltv = (loanAmount / propertyValue) * 100

  // DTI Calculation
  // Front-end DTI usually includes housing costs (P&I + taxes + insurance).
  // For this simplified calc, we'll use P&I + existing debts.
  const totalMonthlyObligations = monthlyPayment + monthlyDebts
  const dti = (totalMonthlyObligations / monthlyIncome) * 100

  // Compliance Checks
  const violations: string[] = []
  if (ltv > 95) violations.push('LTV exceeds 95%')
  if (dti > 43) violations.push('DTI exceeds 43%')
  if (loanAmount > 1000000 && ltv > 80) violations.push('Jumbo loan LTV > 80%')

  return {
    monthlyPayment,
    totalPayment,
    totalInterest,
    ltv,
    dti,
    isCompliant: violations.length === 0,
    violations,
  }
}

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export const formatPercent = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100)
}
